import React, { useState, useCallback } from 'react'
import {
    Card, H5, Row, Col,
    Divider, AnchorButton, ButtonLink, Dialog, Form, H6,
    Table, Spinner, CancelButton, Button, Tooltip,
    Position, FormikMultiSelect
} from '../../../Elements/'
import { Icon, Classes } from '@blueprintjs/core'
import { useDropzone } from 'react-dropzone'
import { Formik, FieldArray } from 'formik'
import { DocumentUploadSchema } from '@mmg/validation'
import { useMutation } from '@apollo/client'
import { gql } from '@apollo/client'
import { useToast } from '../../../Hooks/toast'
import _ from 'lodash'
import { getPresitePath } from '../../../utils/helpers'
import { PresiteDateAndSupervisorColumns, PresiteGuniteFieldsRow, PresitePebbleFieldsRows } from './SharedPresiteForm'

const FRAGMENT = gql`
    fragment JobFormDocumentFragment on Document{
        id
        job_id
        job_detail_id
        document_key
        document_url
        file_name
}  `
const MUTATION = gql`
    mutation InsertDocuments($documents: DocumentUpload!) {
        insertDocuments(documents: $documents) {
         ...JobFormDocumentFragment
        }
    }
    ${FRAGMENT}
`
const GET_PRESIGN = gql`
    mutation GetPresign ($key: String!, $contentType: String) {
        presign(key: $key, contentType: $contentType) {
                url
        }
    }
`

const AttachmentUpload = ({ values, setFieldValue, jobDetailId }) => {
    const setFields = (files) => {
        setFieldValue('last_uploaded_list', files);

        if (files.length !== 0) {
            if (values.document_file_list.length === 0) {
                Array.from(files).forEach(
                    file_data => {
                        values.document_file_list.push({
                            file_data,
                            document_category: { id: 5 },
                            job_type_job_category: { id: jobDetailId }
                        })
                    }
                )
            }
            else {
                const newItems = _.differenceWith(files, values.document_file_list, (a, b) => {
                    return a.name === b.file_data.name && b.file_data.type === a.type && b.file_data.size === a.size
                })
                newItems.forEach(file_data => {
                    values.document_file_list.push({
                        file_data,
                        document_category: { id: 5 },
                        job_type_job_category: { id: jobDetailId }
                    })
                })
            }
        }
    }

    const FileDropzone = () => {
        const onDrop = useCallback(acceptedFiles => {

            setFields(acceptedFiles)
        }, [])
        const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

        return <div {...getRootProps()} className='dropzone'>
            <input {...getInputProps()} />
            {
                isDragActive ?
                    <p>Drop the files here ...</p> : <p>Drag and drop some files here, or <strong>click</strong> to select files</p>
            }
        </div>
    }

    return (
        <>
            <FileDropzone />
        </>
    )
}

const UploadDocumentDialog = ({ isPresiteUploadDialogOpen, setIsPresiteUploadDialogOpen, jobDetailId, isSubmitting, values = {},
    setFieldValue, documentTags }) => {
    return (
        <Dialog
            icon='upload'
            title='Upload Pre-Site Documents'
            isOpen={isPresiteUploadDialogOpen}
            canOutsideClickClose={false}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
        >
            <Form>
                <div className={Classes.DIALOG_BODY}>
                    <Card>
                        <Row>
                            <Col xs={12}>
                                <AttachmentUpload
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    isSubmitting={isSubmitting}
                                    jobDetailId={jobDetailId}
                                />
                            </Col>
                        </Row>
                    </Card>
                    <Card>
                        <Row>
                            <Col xs={12}>
                                <H6>Selected Files</H6>
                            </Col>
                        </Row>
                        <Row>
                            {(!values.document_file_list || values.document_file_list.length === 0) &&
                                <>
                                    <Col xs={12}>
                                        No Files Selected
                                    </Col>
                                </>
                            }

                            {
                                values.document_file_list && values.document_file_list.length > 0 &&
                                <FieldArray
                                    name="document_file_list"
                                    label="Upload Files"
                                    render={({ remove }) => (
                                        <Table
                                            style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        File Name
                                                    </th>
                                                    <th>
                                                        Tags
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.from(values.document_file_list).map((file, idx) => {
                                                    return (

                                                        <tr key={`document_file_list-${idx}`}>
                                                            <td>
                                                                {file.file_data.name}
                                                            </td>
                                                            <td>
                                                                <FormikMultiSelect
                                                                    items={documentTags}
                                                                    name={`document_file_list[${idx}].document_tags`}
                                                                    key={`document_file_list[${idx}]-document-tags`}
                                                                    activeOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <Icon icon='delete' iconSize='20' intent='danger' onClick={() => remove(idx)} />
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                                }
                                            </tbody>
                                        </Table>
                                    )}
                                ></FieldArray>
                            }
                        </Row>
                    </Card>
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px' }}>
                        {isSubmitting && <Spinner size={30} />}
                        <Button type='submit' text='Upload' intent='success' icon='upload' disabled={isSubmitting} />
                        <CancelButton onClick={() => setIsPresiteUploadDialogOpen(false)} disabled={isSubmitting} />
                    </div>
                </div>
            </Form>
        </Dialog>
    )
}

const Upload = ({ isPresiteUploadDialogOpen, setIsPresiteUploadDialogOpen, jobId, jobDetailId,
    documentPresiteTabRefresh, setDocumentPresiteTabRefresh, setIsLoading, filePrefix, documentTags,
    updatePresiteCount }) => {
    const { showError, showSuccess } = useToast()
    const [insertDocuments] = useMutation(MUTATION)
    const [getPresign] = useMutation(GET_PRESIGN)

    const onSubmit = async (props, { resetForm }) => {
        const model = DocumentUploadSchema.cast(props, { stripUnknown: true })
        let uploadPromises = []
        let documentLoading = []
        model.document_file_list.forEach(d => {
            const promise = getPresign({
                variables: {
                    key: `job/${model.job_id}/documents/${filePrefix}${d.file_data.name}`,
                    contentType: d.file_data.type
                }
            })
            uploadPromises.push(promise)
            promise
                .then(res => {
                    let xhr = new XMLHttpRequest()
                    xhr.open('PUT', res.data.presign.url, true)
                    xhr.setRequestHeader('Content-Type', d.file_data.type)
                    xhr.onload = () => {
                        if (xhr.status === 200) {
                            documentLoading.splice(documentLoading.findIndex(t => t === d), 1)
                            setIsLoading(documentLoading.length > 0)
                            console.log('success')
                            if (documentLoading.length === 0) {
                                showSuccess('Document(s) uploaded successfully')
                            }
                        }
                    }
                    xhr.onerror = () => {
                        setIsLoading(false)
                        console.log('error')
                    }
                    xhr.send(d.file_data)
                })
                .catch((err) => {
                    showError(err)
                })
        })

        documentLoading = model.document_file_list
        Promise.all(uploadPromises).then(setIsLoading(documentLoading.length > 0)).then(async () => {
            const mappedModel = {
                job_id: model.job_id,
                document_file_list: model.document_file_list.map(d => {
                    const name = `${filePrefix}${d.file_data.name}`
                    const key = `job/${model.job_id}/documents/${name}`
                    return {
                        job_type_job_category: d.job_type_job_category,
                        file_name: name,
                        file_type: d.file_data.type,
                        key: key,
                        document_tags: d.document_tags
                    }
                })
            }

            await insertDocuments({
                variables: {
                    documents: mappedModel
                }
            }).then((res) => {
                updatePresiteCount(res.data.insertDocuments)

                setIsPresiteUploadDialogOpen(false)
                setDocumentPresiteTabRefresh({
                    ...documentPresiteTabRefresh,
                    documents: res.data.insertDocuments,
                    documentsUpdated: true,
                    key: `documents-${res.data.insertDocuments.length}`
                })

            }).catch(() => {
                showError('An error has occurred.')
            })
        })
    }

    const onReset = () => setIsPresiteUploadDialogOpen(false)

    const initialValues = {
        document_file_list: [],
        last_uploaded_list: [],
        job_id: jobId
    }
    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                onReset={onReset}
                validationSchema={DocumentUploadSchema}
            >
                {({ errors, ...props }) => {
                    return <UploadDocumentDialog
                        isPresiteUploadDialogOpen={isPresiteUploadDialogOpen}
                        setIsPresiteUploadDialogOpen={setIsPresiteUploadDialogOpen}
                        jobId={jobId}
                        jobDetailId={jobDetailId}
                        documentTags={documentTags}
                        {...props}
                    />
                }
                }
            </Formik>
        </>
    )
}

const UpdatePresiteDocumentCount = (values, documents) => {
    const path = getPresitePath(values)
    if (values?.presite) {
        values.presite.document_count = documents.filter(d => d.file_name.startsWith(path)).length
    }
}

export default ({ values, valuePrefix, slurryTypes, vgbMainDrainsBy, jobId, setMainTabState,
    documentPresiteTabRefresh, setDocumentPresiteTabRefresh, presiteSupervisors, employees, documentTags }) => {
    const [presiteVisible, setPresiteVisible] = useState(true)
    const [isPresiteUploadDialogOpen, setIsPresiteUploadDialogOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const jobCategoryId = values?.job_category?.id

    //Get Presite Supervisors for either pebble or gunite, based on the job category
    const companyDivision = jobCategoryId === 1 ? 2 : jobCategoryId === 2 ? 3 : -1
    const supervisorList = employees.filter(e => presiteSupervisors.filter(p => p.company_division_id === companyDivision).map(s => s.id).includes(e.id))

    const namePrefix = `${valuePrefix}.presite`
    return (
        <>
            {isPresiteUploadDialogOpen && <Upload
                isPresiteUploadDialogOpen={isPresiteUploadDialogOpen}
                setIsPresiteUploadDialogOpen={setIsPresiteUploadDialogOpen}
                jobId={jobId}
                jobDetailId={values.id}
                filePrefix={getPresitePath(values)}
                setDocumentPresiteTabRefresh={setDocumentPresiteTabRefresh}
                documentPresiteTabRefresh={documentPresiteTabRefresh}
                setIsLoading={setIsLoading}
                documentTags={documentTags}
                updatePresiteCount={(documents) => UpdatePresiteDocumentCount(values, documents)}
            />
            }
            <Card style={{ width: '100%', margin: 0 }}>
                <Row>
                    <Col xs={12}>
                        <div>
                            <H5 style={{ display: 'inline-block', marginRight: 5 }}>Pre-Site Checklist</H5>
                            {!presiteVisible && <Icon
                                icon='caret-up'
                                iconSize='20'
                                onClick={() => setPresiteVisible(true)}
                            />
                            }
                            {presiteVisible && <Icon icon='caret-down' iconSize={20} onClick={() => setPresiteVisible(false)} />}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <PresiteDateAndSupervisorColumns
                        namePrefix={namePrefix}
                        supervisorList={supervisorList}
                    />
                    <Col xs={12} sm={12} med={6} lg={6}>
                        <div style={{ float: 'right' }}>
                            <ButtonLink to=''
                                onClick={() => {
                                    setDocumentPresiteTabRefresh({
                                        ...documentPresiteTabRefresh,
                                        presiteDefaultTab: true,
                                        openFolder: getPresitePath(values)
                                    });
                                    setMainTabState('document_tab')
                                }}
                                intent={'primary'}
                                style={{ margin: '20px 5px 0px' }}
                                minimal
                                disabled={isLoading}
                                text={isLoading ? '...Uploading Pre-Site documents' : `View ${values?.presite?.document_count || '0'} Pre-Site Document(s)`}
                            />
                            <Tooltip
                                content={`Please ${!values.id || values.id === 0 ? 'save the job' : 'select Pre-Site date'} before uploading Pre-Site documents.`}
                                position={Position.TOP}
                                disabled={values.id > 0 && (values.presite && values.presite.checkin_date)}>
                                <AnchorButton
                                    text='Upload Pre-Site Documents'
                                    icon='upload'
                                    intent='primary'
                                    style={{ margin: '20px 5px 0px' }}
                                    onClick={() => setIsPresiteUploadDialogOpen(true)}
                                    disabled={!values.id || values.id === 0 || (values.presite && !values.presite.checkin_date)}
                                />
                            </Tooltip>
                        </div>
                    </Col>
                </Row>
                <Divider />
                {presiteVisible && <>
                    {jobCategoryId === 1 && //gunite
                        <PresiteGuniteFieldsRow
                            values={values}
                            namePrefix={namePrefix}
                        />
                    }
                    {jobCategoryId === 2 && //pebble
                        <PresitePebbleFieldsRows
                            values={values}
                            namePrefix={namePrefix}
                            jobDetailId={values.id}
                            slurryTypes={slurryTypes}
                            vgbMainDrainsBy={vgbMainDrainsBy}
                        />
                    }
                </>}
            </Card>
        </>
    )
}